/**
 * Sanitized submit telemetry for the static SPA MVP.
 *
 * There is no telemetry vendor SDK, backend, or queue in this MVP (see
 * `docs/telemetry-and-error-handling.md`). This module produces a single,
 * strongly-typed, non-secret event describing how a referral submit resolved
 * and hands it to an injectable sink (default: a sanitized `console.info`).
 *
 * A telemetry event carries ONLY:
 * - `event`         — a stable event name,
 * - `outcome`       — a sanitized outcome category (success or error kind),
 * - `statusClass`   — the HTTP status class (e.g. "4xx"), when a response arrived,
 * - `correlationId` — the same id sent on the LeadIntake request header and
 *                     outward postMessage, for cross-surface correlation.
 *
 * It never contains borrower PII, the API key/auth header, the raw request or
 * response body, exact HTTP status codes, stack traces, or the Partner
 * Reference ID.
 */
import type { SubmitOutcomeCategory } from "./api";
import { contain } from "./callbacks";

/** Stable event name for a single referral submit attempt outcome. */
export const TELEMETRY_EVENT_SUBMIT = "turbovets.referral.submit";

/** A sanitized, monitoring-safe submit telemetry event. */
export interface SubmitTelemetryEvent {
  event: typeof TELEMETRY_EVENT_SUBMIT;
  outcome: SubmitOutcomeCategory;
  /** HTTP status class ("2xx".."5xx"); omitted when no response was received. */
  statusClass?: string;
  correlationId: string;
}

/**
 * Reduces an exact HTTP status code to its non-specific class ("2xx", "4xx",
 * "5xx", ...). Returns `undefined` for missing or out-of-range codes so callers
 * never emit a bare number.
 */
export function httpStatusClass(status?: number): string | undefined {
  if (typeof status !== "number" || !Number.isInteger(status)) return undefined;
  if (status < 100 || status > 599) return undefined;
  return `${Math.floor(status / 100)}xx`;
}

export interface SubmitTelemetryInput {
  outcome: SubmitOutcomeCategory;
  correlationId: string;
  /** Exact HTTP status when a response arrived; reduced to a status class. */
  status?: number;
}

/**
 * Builds a sanitized submit telemetry event. Only the allow-listed fields above
 * are ever set, so the result cannot carry PII, secrets, or raw payloads.
 */
export function buildSubmitTelemetryEvent(input: SubmitTelemetryInput): SubmitTelemetryEvent {
  const event: SubmitTelemetryEvent = {
    event: TELEMETRY_EVENT_SUBMIT,
    outcome: input.outcome,
    correlationId: input.correlationId,
  };
  const statusClass = httpStatusClass(input.status);
  if (statusClass) event.statusClass = statusClass;
  return event;
}

/**
 * Consumer of built telemetry events (a logger, beacon, or test spy).
 *
 * The `void` return keeps every existing sink assignable, but it also means an
 * `async` sink is accepted here: such a sink fails by returning a rejected
 * promise rather than by throwing. {@link emitSubmitTelemetry} therefore invokes
 * the sink totally — it never awaits it, and neither a throw nor a rejection can
 * reach the caller or escape as an unhandled rejection.
 */
export type TelemetrySink = (event: SubmitTelemetryEvent) => void;

export interface EmitTelemetryOptions {
  /** Runtime `telemetryEnabled` flag; when false, nothing is emitted. */
  enabled: boolean;
  sink?: TelemetrySink;
}

/**
 * Emits a submit telemetry event through the configured sink when telemetry is
 * enabled. Returns the event that was emitted, or `undefined` when disabled.
 */
export function emitSubmitTelemetry(
  input: SubmitTelemetryInput,
  options: EmitTelemetryOptions,
): SubmitTelemetryEvent | undefined {
  if (!options.enabled) return undefined;
  const event = buildSubmitTelemetryEvent(input);
  // Emitting is best-effort: a failing sink must never break a submission, and
  // an `async` sink's rejection must never escape as an unhandled rejection.
  contain(() => (options.sink ?? defaultTelemetrySink)(event));
  return event;
}

/**
 * Default sink: a single sanitized structured line. Because it only serializes
 * a `SubmitTelemetryEvent`, it cannot log PII, secrets, or raw bodies.
 */
function defaultTelemetrySink(event: SubmitTelemetryEvent): void {
  if (typeof console !== "undefined" && typeof console.info === "function") {
    console.info("[turbovets-telemetry]", JSON.stringify(event));
  }
}
